/**
 * Pure path helpers for PumpIO treemap navigation.
 * Tree order puts primary sectors (Electronic technology, Technology services)
 * before thematic groups (AI, Defense), so first preorder match is preferred.
 */

/**
 * Path of node names from root to the first leaf matching ticker (inclusive).
 * @returns {string[] | null}
 */
export function findTickerPath(root, ticker) {
  if (!root || !ticker) return null;
  const target = String(ticker).trim();

  function walk(node, path) {
    const next = [...path, node.name];
    if (!node.children?.length) {
      const name = node.name.split('\n')[0].trim();
      if (name === target) return next;
      return null;
    }
    for (const child of node.children) {
      const found = walk(child, next);
      if (found) return found;
    }
    return null;
  }

  return walk(root, []);
}

/**
 * Resolve click navigation for a mover ticker:
 * peerPath = ancestors through the industry/group parent (siblings visible when zoomed there).
 * leafPath includes the ticker leaf.
 *
 * @returns {{ ticker: string, leafPath: string[], peerPath: string[], peerParentName: string } | null}
 */
export function resolveMoverNavigation(root, ticker) {
  const leafPath = findTickerPath(root, ticker);
  if (!leafPath || leafPath.length < 2) return null;

  const peerPath = leafPath.slice(0, -1);
  return {
    ticker: String(ticker).trim(),
    leafPath,
    peerPath,
    peerParentName: peerPath[peerPath.length - 1],
  };
}

/**
 * Walk a tree by name path (root name is path[0]; start matching from path[1]).
 * Same semantics as the chart view-root restorer in App.jsx for ancestor paths
 * that already start at the current root node.
 */
export function findNodeByPath(root, pathNames) {
  if (!root || !pathNames?.length) return root;
  let node = root;
  for (let i = 1; i < pathNames.length; i += 1) {
    const child = node.children?.find((entry) => entry.name === pathNames[i]);
    if (!child) return node;
    node = child;
  }
  return node;
}

/**
 * Resolve a node on the live ECharts treemap tree.
 *
 * TreemapSeries creates a **virtual root** whose children are series.data
 * (see TreemapSeries.getInitialData). So `US Market` is virtualRoot.children[0],
 * not virtualRoot itself. Walk every segment of pathNames as a successive child
 * starting from the virtual root — do NOT skip pathNames[0].
 *
 * @returns {object | null} the matched node, or null if any segment is missing
 */
export function findEchartsNodeByNamePath(virtualRoot, pathNames) {
  if (!virtualRoot || !pathNames?.length) return null;
  let node = virtualRoot;
  for (const name of pathNames) {
    const child = node.children?.find((entry) => entry.name === name);
    if (!child) return null;
    node = child;
  }
  return node;
}

/**
 * Documented ECharts actions for mover click (shapes only; targetNode attached at runtime).
 * Zoom to peer parent so industry siblings stay visible; highlight the leaf ticker.
 */
export function buildMoverClickActionSpecs(nav) {
  if (!nav) return null;
  return {
    zoom: {
      type: 'treemapZoomToNode',
      seriesIndex: 0,
      targetPath: nav.peerPath,
    },
    downplay: {
      type: 'downplay',
      seriesIndex: 0,
    },
    highlight: {
      type: 'highlight',
      seriesIndex: 0,
      name: nav.ticker,
    },
  };
}

/**
 * Zoom treemap to the industry/sector parent that contains ticker among peers,
 * then highlight the leaf. Pure orchestration over chart + treeData.
 * Returns false if navigation cannot be resolved.
 */
export function navigateToMoverInPeerGroup(chart, treeData, ticker) {
  if (!chart || !treeData || !ticker) return false;

  const nav = resolveMoverNavigation(treeData, ticker);
  const specs = buildMoverClickActionSpecs(nav);
  if (!nav || !specs) return false;

  const seriesModel = chart.getModel?.()?.getSeriesByIndex?.(0);
  if (!seriesModel) return false;

  const tree = seriesModel.getData?.()?.tree;
  if (!tree?.root) return false;

  // ECharts virtual root: walk full peerPath including 'US Market'
  const echartsNode = findEchartsNodeByNamePath(tree.root, nav.peerPath);
  if (!echartsNode) return false;

  chart.dispatchAction({
    type: specs.zoom.type,
    seriesIndex: specs.zoom.seriesIndex,
    targetNode: echartsNode,
  });

  chart.dispatchAction({
    type: specs.downplay.type,
    seriesIndex: specs.downplay.seriesIndex,
  });

  chart.dispatchAction({
    type: specs.highlight.type,
    seriesIndex: specs.highlight.seriesIndex,
    name: specs.highlight.name,
  });

  return true;
}
